import { displayErrors } from "common/helpers";
import { cubesService as requestsSvc, lookupSvc } from "services/serviceStorage";
import { scopes } from "common/enums";
import {
    LookupsResponse,
    Library,
    CubeGroup,
    FilterState,
    Cube,
    Report,
    File,
    TemplateEditModalState
} from "./types";
import { BaseApiResponse } from "common/types";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import find from "lodash/find";

export const fetchLookups = createAsyncThunk<LookupsResponse, number>(
    "cubeGroups/fetchLookups",
    async (taskId, { rejectWithValue }) => {
        try {
            const [taskRes, scopesRes, librariesRes] = await Promise.all([
                lookupSvc.getReportingLookup(taskId),
                lookupSvc.getScopes(),
                lookupSvc.getLibraries(taskId)
            ]);

            return {
                task: taskRes.data.result,
                scopes: scopesRes.data.result,
                libraries: librariesRes.data.result.map((l: Library) => ({ ...l, idx: l.libraryId })),
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error)
        }
    }
);

// request master source
export const fetchTemplates = createAsyncThunk<{ data: CubeGroup[]; total: number; filter: FilterState }, FilterState>(
    'cubeGroups/fetchTemplates',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: CubeGroup[]; totalCount: number }>>({
            handler: () => requestsSvc.getTemplates(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(i => ({ ...i, idx: i.templateId })) || [],
            total: data?.result.totalCount || 0,
            filter: filter,
        };

        return mappedData;
    }
);

// request details source
export const fetchCubes = createAsyncThunk<{ data: Cube[]; total: number; filter: FilterState }, FilterState>(
    'cubeGroups/fetchCubes',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Cube[]; totalCount: number }>>({
            handler: () => requestsSvc.getCubes(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(i => ({ ...i, idx: i.cubeId })) || [],
            total: data?.result.totalCount || 0,
            filter: filter,
        };

        return mappedData;
    }
);

// request reports source
export const fetchReports = createAsyncThunk<{ data: Report[]; }, { templateId: number; cubeId: number; libraries: Library[] }>(
    'cubeGroups/fetchReports',
    async ({ templateId, cubeId, libraries }) => {
        const { data } = await wrapRequest<BaseApiResponse<{ fileId: number; scopeId: number }[]>>({
            handler: () => requestsSvc.getTemplateReports(templateId, cubeId),
            showNotifications: true
        });

        const mappedData = {
            data: libraries.map(l => ({
                idx: l.libraryId,
                libraryId: l.libraryId,
                libraryName: l.libraryName,
                files: l.libraryFiles.map(f => ({
                    scopeId: (find(data?.result, r => r.fileId === f.libraryFileId) || { scopeId: scopes.notUsed }).scopeId,
                    fileName: f.displayName,
                    fileId: f.libraryFileId,
                    hasAggregationScope: f.hasAggregationScope,
                })),
            })),
        };

        return mappedData;
    }
);

// create/update template
export const saveTemplate = createAsyncThunk<CubeGroup, TemplateEditModalState['source'] & { taskId: number }>(
    'cubeGroups/saveTemplate',
    async (template) => {
        const { data } = await wrapRequest<BaseApiResponse<CubeGroup>>({
            handler: () => requestsSvc.saveTemplate(template),
            showNotifications: true
        });

        return data?.result!
    }
);

// delete template
export const deleteTemplates = createAsyncThunk<void, number[]>(
    'cubeGroups/deleteTemplates',
    async (templateIds, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.deleteTemplates(templateIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// get template
export const getTemplate = createAsyncThunk<CubeGroup, number>(
    'cubeGroups/getTemplate',
    async (templateId) => {
        const { data } = await wrapRequest<BaseApiResponse<CubeGroup>>({
            handler: () => requestsSvc.getTemplate(templateId),
            showNotifications: true
        });

        return data?.result!
    }
);

// get default reports
export const getDefaultReports = createAsyncThunk<{ fileId: number; scopeId: number; }[], number>(
    'cubeGroups/getDefaultReports',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<{ fileId: number; scopeId: number; }[]>>({
            handler: () => requestsSvc.getDefaultReports(taskId),
            showNotifications: true
        });

        return data?.result!
    }
);

// save default reports
export const saveDefaultReports = createAsyncThunk<void, { taskId: number; reports: File[] }>(
    'cubeGroups/saveDefaultReports',
    async ({ taskId, reports }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.saveDefaultReports(taskId, reports),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// save reports changes
export const saveTemplateReports = createAsyncThunk<void, { templateId: number; cubeIds: number[]; reports: File[] }>(
    'cubeGroups/saveTemplateReports',
    async ({ templateId, cubeIds, reports }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.saveTemplateReports(templateId, cubeIds, reports),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// delete reports
export const deleteTemplateReports = createAsyncThunk<void, { templateId: number; cubeIds: number[] }>(
    'cubeGroups/saveTemplateReports',
    async ({ templateId, cubeIds }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.deleteTemplateReports(templateId, cubeIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);